// 전역 실시간 알림 — 어느 화면에 있든 새 채팅/메일이 오면 우측 하단 팝업 + OS 알림을
// 띄우고, 데스크톱(Electron) 아이콘/작업표시줄에 안읽음 배지 숫자를 표시한다.
//   · 채팅: 항상 켜진 WebSocket 구독(현재 보고 있는 방 제외)
//   · 메일: /mail/unread-count 폴링(증가 시 알림)
//   · 배지: navigator.setAppBadge + Electron 브리지(window.timbox.setBadge)
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../shared/api/client";
import { useAuth } from "../shared/stores/auth";

type Pop = { id: string; kind: "chat" | "mail"; title: string; body: string; to: string };

// 발신자 표기 — "홍길동 <a@b>" 형태면 이름만, 아니면 주소 그대로 보여 준다.
function senderBrief(addr: string): string {
  const m = (addr || "").match(/^\s*"?([^"<]+?)"?\s*<[^>]+>/);
  if (m && m[1].trim()) return m[1].trim();
  return addr || "알 수 없는 발신자";
}

// 데스크톱 작업표시줄 오버레이용 배지 PNG(빨간 원 + 흰 숫자)를 렌더러에서 그린다.
function makeBadgePng(n: number): string | undefined {
  try {
    const c = document.createElement("canvas");
    c.width = 32; c.height = 32;
    const ctx = c.getContext("2d");
    if (!ctx) return undefined;
    ctx.beginPath(); ctx.arc(16, 16, 15, 0, Math.PI * 2); ctx.fillStyle = "#EF4444"; ctx.fill();
    const label = n > 99 ? "99+" : String(n);
    ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.font = `bold ${label.length >= 3 ? 12 : 20}px sans-serif`;
    ctx.fillText(label, 16, 17);
    return c.toDataURL("image/png");
  } catch { return undefined; }
}

export function RealtimeNotifier() {
  const token = useAuth((s) => s.accessToken);
  const me = useAuth((s) => s.user);
  const nav = useNavigate();
  const loc = useLocation();

  const [pops, setPops] = useState<Pop[]>([]);
  const [mailUnread, setMailUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const mailBaseline = useRef<number | null>(null);   // 첫 로드값 — 이땐 알리지 않는다
  const wsRef = useRef<WebSocket | null>(null);
  const locRef = useRef(loc);
  locRef.current = loc;

  // 알림 권한 요청(한 번). 데스크톱/브라우저 OS 알림에 필요.
  useEffect(() => {
    try { if (typeof Notification !== "undefined" && Notification.permission === "default") Notification.requestPermission(); } catch { /* noop */ }
  }, []);

  const pushPop = (p: Omit<Pop, "id">) => {
    const id = Math.random().toString(36).slice(2);
    setPops((cur) => [...cur.slice(-3), { ...p, id }]);
    window.setTimeout(() => setPops((cur) => cur.filter((x) => x.id !== id)), 6500);
  };
  const osNotify = (title: string, body: string, to: string) => {
    try {
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      if (document.hasFocus()) return;   // 창을 보고 있으면 인앱 팝업으로 충분
      const n = new Notification(title, { body, icon: "/timple-logo.jpg", tag: to });
      n.onclick = () => {
        // 데스크톱 앱에서는 창이 트레이로 숨어 있을 수 있다. 그때는 window.focus()
        // 만으로는 창이 다시 나타나지 않으므로, 프로그램 쪽에 띄워 달라고 부탁한다.
        try { (window as any).timbox?.showWindow?.(); } catch { /* 웹 브라우저에는 없다 */ }
        window.focus();
        nav(to);
        n.close();
      };
    } catch { /* noop */ }
  };
  const notify = (kind: "chat" | "mail", title: string, body: string, to: string) => {
    pushPop({ kind, title, body, to });
    osNotify(title, body, to);
  };
  // 채팅 안읽음은 서버 기준(방별 읽음 커서 합계). 배지 정확도를 위해 여기서만 갱신한다.
  const refetchChat = async () => {
    try { const r = await api.get<{ count: number }>("/chat/unread-count"); setChatUnread(r.data?.count ?? 0); }
    catch { /* ignore */ }
  };

  // ── 채팅 WebSocket (항상 켜둔다) ────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    let closed = false;
    let retry: number | undefined;
    let attempt = 0;
    const connect = () => {
      if (closed) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const base = (import.meta as any).env?.VITE_WS_URL ?? `${proto}://${location.host}/ws`;
      // 토큰은 붙기 직전에 스토어에서 다시 읽는다. 훅이 잡아 둔 값으로 재연결하면
      // 만료된 뒤에는 서버가 4401 로 끊고, 같은 죽은 토큰으로 계속 두드리게 된다.
      const tk = useAuth.getState().accessToken || token;
      const ws = new WebSocket(`${base}/chat?token=${encodeURIComponent(tk)}`);
      wsRef.current = ws;
      ws.onopen = () => { attempt = 0; };
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type !== "message:new") return;
          const p = ev.payload || {};
          if (p.sender_id === (me?.id ?? 0)) return;             // 내가 보낸 건 제외
          if (p.type === "BOT") return;                          // 봇 응답은 배지/팝업 제외
          const l = locRef.current;
          const viewing = l.pathname === "/chat"
            && new URLSearchParams(l.search).get("room") === String(p.room_id);
          void refetchChat();                                    // 배지는 서버 기준으로 갱신
          if (viewing && document.hasFocus()) return;            // 그 방을 보고 있으면 팝업 생략
          // 어느 방에서 누가 보냈는지 보이게 한다. 단톡방은 '방 이름' 아래
          // '보낸사람: 내용', 1:1 은 '보낸사람' 아래 '내용'(서버가 이름을 실어 보낸다).
          const text = (p.content || "").trim().slice(0, 80) || "📎 파일을 보냈습니다";
          const who = p.sender_name || "";
          const title = p.room_name || who || "새 채팅 메시지";
          const body = p.is_group && who ? `${who}: ${text}` : text;
          notify("chat", title, body, `/chat?room=${p.room_id}`);
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (closed) return;
        attempt += 1;
        // 지수 백오프(1s→30s). 서버가 내려가 있는 동안 4초마다 두드려 봐야 소용없다.
        retry = window.setTimeout(connect, Math.min(30000, 1000 * 2 ** Math.min(attempt, 5)));
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => { closed = true; if (retry) window.clearTimeout(retry); try { wsRef.current?.close(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, me?.id]);

  // ── 메일 안읽음 폴링 ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get<{ count: number }>("/mail/unread-count");
        if (!alive) return;
        const n = r.data?.count ?? 0;
        setMailUnread(n);
        if (mailBaseline.current == null) { mailBaseline.current = n; return; }
        const prev = mailBaseline.current;
        // 아래에서 목록 조회를 기다리는 동안(포커스 폴링과 겹칠 수 있다) 다른 폴이
        // 같은 증가분을 또 알리지 않도록, 기준값을 먼저 올려 둔다.
        mailBaseline.current = n;
        if (n > prev) {
          const delta = n - prev;
          // 팝업에 발신자·제목을 싣는다 — 방금 도착한 안읽은 메일 최신 몇 통 조회.
          // 목록 조회가 실패해도 알림 자체는 놓치지 않도록 통수 문구로 폴백한다.
          try {
            const list = await api.get<{ data: { id: number; from_addr: string; subject: string | null }[] }>(
              `/mail?folder=UNREAD&page=1&size=${Math.min(delta, 3)}&sort=date&sort_dir=desc`);
            const mails = (list.data?.data ?? []).slice(0, 3);
            if (!alive) return;
            if (mails.length === 0) throw new Error("empty");
            // 오래된 것부터 띄워 최신 메일이 마지막(맨 아래)에 오게 한다.
            for (const m of [...mails].reverse()) {
              notify("mail", `새 메일 · ${senderBrief(m.from_addr)}`,
                m.subject?.trim() || "(제목 없음)", `/mail?open=${m.id}`);
            }
            if (delta > mails.length) {
              notify("mail", "새 메일", `외 ${delta - mails.length}통이 더 도착했습니다`, "/mail?filter=UNREAD");
            }
          } catch {
            notify("mail", "새 메일", `읽지 않은 메일 ${delta}통이 도착했습니다`, "/mail?filter=UNREAD");
          }
        }
      } catch { /* ignore */ }
    };
    poll();
    const iv = window.setInterval(poll, 25_000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; window.clearInterval(iv); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 채팅 안읽음 폴링(서버 기준) — WS가 끊겼거나 다른 기기에서 읽은 경우도 반영.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const poll = () => { if (alive) void refetchChat(); };
    poll();
    const iv = window.setInterval(poll, 25_000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; window.clearInterval(iv); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 화면(방) 이동 시 재조회 — 방을 열면 서버가 읽음 처리하므로 배지가 줄어든다.
  useEffect(() => {
    const t = window.setTimeout(() => { void refetchChat(); }, 900);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, loc.search]);

  // ── 배지(아이콘 숫자) ───────────────────────────────────────────────────────
  useEffect(() => {
    const total = mailUnread + chatUnread;
    try {
      if (total > 0) (navigator as any).setAppBadge?.(total);
      else (navigator as any).clearAppBadge?.();
    } catch { /* noop */ }
    try { (window as any).timbox?.setBadge?.(total, total > 0 ? makeBadgePng(total) : undefined); } catch { /* noop */ }
  }, [mailUnread, chatUnread]);

  if (pops.length === 0) return null;
  return (
    <div style={{ position: "fixed", right: 16, bottom: 16, zIndex: 3000, display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end", pointerEvents: "none" }}>
      {pops.map((p) => (
        <div key={p.id}
          onClick={() => { setPops((cur) => cur.filter((x) => x.id !== p.id)); nav(p.to); }}
          style={{
            pointerEvents: "auto", cursor: "pointer", width: 320, maxWidth: "90vw",
            background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 12,
            boxShadow: "0 12px 32px rgba(0,0,0,0.22)", padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start",
          }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, background: p.kind === "mail" ? "rgba(59,130,246,0.14)" : "rgba(99,102,241,0.14)" }}>
            {p.kind === "mail" ? "✉️" : "💬"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.body}</div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); setPops((cur) => cur.filter((x) => x.id !== p.id)); }}
            style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 16, lineHeight: 1, padding: 0 }}>✕</button>
        </div>
      ))}
    </div>
  );
}
